import { Component } from "react";
import { DataTable, DataTablePageEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { SelectItem } from "primereact/selectitem";

import { AppCard } from "../../components/ui";
import { paginatorTemplate } from "../Paginator";
import { dateBodyTemplate, formatDateTimeLocal } from "../../utils/columnTemplates";
import { toastService } from "../../services/toastService";
import { cashOperationsApiService } from "../../services/api/operations/CashOperationsApiService";
import { merchantApiService } from "../../services/api/merchants/MerchantApiService";
import { isSuperAdmin, isOperationOfficer } from "../../authGuard";
import { CashOperation, CashOperationsSearchRequest } from "../../models/CashOperation";

type FilterOption = { label: string; value: string | null };

type CashOperationsState = {
    isLoading: boolean;
    items: CashOperation[];
    totalRecords: number;
    first: number;
    rows: number;
    status: string | null;
    cashDirection: string | null;
    search: string;
    merchantId: string;
    merchants: SelectItem[];
    selected: CashOperation | null;
    showDetails: boolean;
    retryingId: string | null;
};

// Super Admins can scope the list to a single merchant; "" means all merchants
// (the adapter treats null/0/"" as "all"). Only shown to Super Admins — other
// operators (e.g. Operation Officer) always see the full cross-merchant list.
const ALL_MERCHANTS: SelectItem = { label: "All merchants", value: "" };

// TNPDEVAPPS-1863: only statuses a cash intent can actually rest in. "Created" and "CodeIssued" are
// omitted because the adapter moves the intent to "Awaited" (or "Failed") before it settles, so neither
// ever appears in the list. "Expired" is now a real, distinct state (the webhook no longer collapses
// expiry into Cancelled).
const statusOptions: FilterOption[] = [
    { label: "All", value: null },
    { label: "Awaited", value: "Awaited" },
    { label: "Paid", value: "Paid" },
    { label: "Failed", value: "Failed" },
    { label: "Expired", value: "Expired" },
    { label: "Cancelled", value: "Cancelled" },
];

const directionOptions: FilterOption[] = [
    { label: "All", value: null },
    { label: "Cash In", value: "CashIn" },
    { label: "Cash Out", value: "CashOut" },
];

class CashOperations extends Component<{}, CashOperationsState> {
    constructor(props: any | Readonly<any>) {
        super(props);
        this.state = {
            isLoading: false,
            items: [],
            totalRecords: 0,
            first: 0,
            rows: 10,
            status: null,
            cashDirection: null,
            search: "",
            merchantId: "",
            merchants: [ALL_MERCHANTS],
            selected: null,
            showDetails: false,
            retryingId: null,
        };
    }

    componentDidMount(): void {
        this.loadMerchants();
        this.load();
    }

    // Cross-merchant operators (Super Admin, Operation Officer) get the merchant picker to narrow
    // the all-merchants list to one merchant (TNPDEVAPPS-1812). Other roles keep the default view.
    loadMerchants = async () => {
        if (!isSuperAdmin() && !isOperationOfficer()) return;
        const result = await merchantApiService.getSelectItem();
        if (!result.success) {
            toastService.error(result.message ?? "Failed to load merchants.");
            return;
        }
        this.setState({ merchants: [ALL_MERCHANTS, ...(result.data ?? [])] });
    };

    load = async () => {
        this.setState({ isLoading: true });

        const request = new CashOperationsSearchRequest();
        request.pageNumber = Math.floor(this.state.first / this.state.rows) + 1;
        request.pageSize = this.state.rows;
        request.status = this.state.status ?? undefined;
        request.cashDirection = this.state.cashDirection ?? undefined;
        request.search = this.state.search?.trim() || undefined;
        // "" -> adapter returns all merchants; a specific id scopes to that merchant.
        request.merchantId = this.state.merchantId || undefined;

        const result = await cashOperationsApiService.getAllPaged(request);

        if (!result.success) {
            toastService.error(result.message ?? "Failed to load cash operations.");
            this.setState({ isLoading: false, items: [], totalRecords: 0 });
            return;
        }

        this.setState({
            isLoading: false,
            items: result.data ?? [],
            totalRecords: result.totalCount ?? 0,
        });
    };

    onPage = (event: DataTablePageEvent) => {
        this.setState({ first: event.first, rows: event.rows }, this.load);
    };

    applyFilters = () => {
        this.setState({ first: 0 }, this.load);
    };

    resetFilters = () => {
        this.setState({ status: null, cashDirection: null, search: "", merchantId: "", first: 0 }, this.load);
    };

    onRetry = async (row: CashOperation) => {
        // Guard against a second click while a retry is already in flight.
        if (this.state.retryingId) return;

        // Disable the button (and show a spinner) until the response comes back.
        this.setState({ retryingId: row.id });
        const result = await cashOperationsApiService.retry(row.id);

        if (!result.success) {
            this.setState({ retryingId: null });
            toastService.error(result.message ?? "Retry failed.");
            return;
        }

        // On success, refresh the page so the list shows the latest status.
        // Keep the button disabled (retryingId stays set) through the reload.
        window.location.reload();
    };

    showDetail = (row: CashOperation) => this.setState({ selected: row, showDetails: true });
    hideDetail = () => this.setState({ showDetails: false });

    statusBadge = (status: string) => {
        const map: Record<string, string> = {
            Paid: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
            Failed: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
            Expired: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
            Cancelled: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
        };
        const cls = map[status] ?? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300";
        return <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold tracking-wide ${cls}`}>{status}</span>;
    };

    // "Needs review" must only surface when an operator actually has to act — i.e. a Failed
    // deposit (also when canRetry is true). A successful, booked deposit shows "Reviewed"; anything
    // still in flight (Awaited/Created/CodeIssued) or terminal-but-not-actionable (Expired/Cancelled)
    // is neutral, so we don't flag pending deposits as needing attention.
    reviewedBadge = (row: CashOperation) => {
        if (row.status === "Failed") {
            return <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">Needs review</span>;
        }
        if (row.reviewed) {
            return <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">Reviewed</span>;
        }
        return <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-semibold bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">—</span>;
    };

    amountBody = (row: CashOperation) => <span>{row.amount?.toFixed(2)} {row.currency}</span>;
    feeBody = (row: CashOperation) => <span>{row.fee != null ? row.fee.toFixed(2) : "—"}</span>;

    actionsBody = (row: CashOperation) => (
        <div className="flex gap-2">
            <Button label="Details" size="small" outlined onClick={() => this.showDetail(row)} />
            <Button
                label="Retry"
                size="small"
                severity="warning"
                disabled={!row.canRetry || this.state.retryingId !== null}
                loading={this.state.retryingId === row.id}
                onClick={() => this.onRetry(row)}
            />
        </div>
    );

    detailRow = (label: string, value: React.ReactNode) => (
        <div className="flex justify-between gap-4 py-1 border-b border-gray-100 dark:border-gray-800">
            <span className="text-sm text-gray-500">{label}</span>
            <span className="text-sm font-medium text-right break-all">{value ?? "—"}</span>
        </div>
    );

    render() {
        const s = this.state.selected;
        return (
            <AppCard className="mb-3">
                <AppCard.Header title="Cash Operations" />
                <AppCard.Body>
                    <div className="flex flex-wrap items-end gap-3 mb-3">
                        {(isSuperAdmin() || isOperationOfficer()) && (
                            <div className="flex flex-col">
                                <label className="text-xs text-gray-500 mb-1">Merchant</label>
                                <Dropdown
                                    value={this.state.merchantId}
                                    options={this.state.merchants}
                                    optionLabel="label"
                                    optionValue="value"
                                    onChange={(e) => this.setState({ merchantId: e.value ?? "" })}
                                    filter
                                    placeholder="All merchants"
                                    className="w-56"
                                />
                            </div>
                        )}
                        <div className="flex flex-col">
                            <label className="text-xs text-gray-500 mb-1">Status</label>
                            <Dropdown value={this.state.status} options={statusOptions} optionLabel="label" optionValue="value" onChange={(e) => this.setState({ status: e.value })} placeholder="All" className="w-44" />
                        </div>
                        <div className="flex flex-col">
                            <label className="text-xs text-gray-500 mb-1">Direction</label>
                            <Dropdown value={this.state.cashDirection} options={directionOptions} optionLabel="label" optionValue="value" onChange={(e) => this.setState({ cashDirection: e.value })} placeholder="All" className="w-40" />
                        </div>
                        <div className="flex flex-col">
                            <label className="text-xs text-gray-500 mb-1">Search</label>
                            <InputText
                                value={this.state.search}
                                onChange={(e) => this.setState({ search: e.target.value })}
                                onKeyDown={(e) => { if (e.key === "Enter") this.applyFilters(); }}
                                placeholder="Ref / code / customer"
                                className="w-64"
                            />
                        </div>
                        <Button label="Apply" icon="pi pi-search" onClick={this.applyFilters} />
                        <Button label="Reset" outlined onClick={this.resetFilters} />
                    </div>

                    <DataTable
                        value={this.state.items}
                        stripedRows
                        lazy
                        paginator
                        paginatorTemplate={paginatorTemplate}
                        first={this.state.first}
                        rows={this.state.rows}
                        rowsPerPageOptions={[10, 20, 50]}
                        totalRecords={this.state.totalRecords}
                        onPage={this.onPage}
                        loading={this.state.isLoading}
                        emptyMessage="No results found"
                    >
                        <Column field="externalRef" header="External Ref" />
                        <Column field="amount" header="Amount" body={this.amountBody} />
                        <Column field="fee" header="Fee" body={this.feeBody} />
                        <Column field="status" header="Status" body={(r: CashOperation) => this.statusBadge(r.status)} />
                        <Column field="epayStatus" header="ePay" body={(r: CashOperation) => <span>{r.epayStatus ?? "—"}</span>} />
                        <Column field="paymentCode" header="Code" body={(r: CashOperation) => <span>{r.paymentCode ?? "—"}</span>} />
                        <Column field="customerNumber" header="Customer" body={(r: CashOperation) => <span>{r.customerNumber ?? "—"}</span>} />
                        <Column field="reviewed" header="Review" body={(r: CashOperation) => this.reviewedBadge(r)} />
                        <Column field="createdAt" header="Created" body={dateBodyTemplate("createdAt")} />
                        <Column header="Actions" body={this.actionsBody} />
                    </DataTable>
                </AppCard.Body>

                <Dialog header="Cash Operation Details" visible={this.state.showDetails} style={{ width: "40rem" }} onHide={this.hideDetail}>
                    {s && (
                        <div className="flex flex-col">
                            {this.detailRow("External Ref", s.externalRef)}
                            {this.detailRow("Status", s.status)}
                            {this.detailRow("Direction", s.cashDirection)}
                            {this.detailRow("Amount", `${s.amount?.toFixed(2)} ${s.currency}`)}
                            {this.detailRow("Fee", s.fee != null ? s.fee.toFixed(2) : "—")}
                            {this.detailRow("Payment Code", s.paymentCode)}
                            {this.detailRow("ePay Status", s.epayStatus)}
                            {this.detailRow("Reviewed", s.reviewed ? "Yes" : "No")}
                            {this.detailRow("Branch", s.branchCode)}
                            {this.detailRow("Customer Number", s.customerNumber)}
                            {this.detailRow("Account Number", s.accountNumber)}
                            {this.detailRow("Individual Id", s.individualId)}
                            {this.detailRow("FlexCube Ref", s.flexCubeTransactionReference)}
                            {this.detailRow("Executing Operator", s.executingOperatorId)}
                            {this.detailRow("Failure Reason", s.failureReason)}
                            {this.detailRow("Created", formatDateTimeLocal(s.createdAt))}
                            {this.detailRow("Updated", formatDateTimeLocal(s.updatedAt))}
                            {this.detailRow("Expires", formatDateTimeLocal(s.expiresAt))}
                        </div>
                    )}
                </Dialog>
            </AppCard>
        );
    }
}

export default CashOperations;
